import { supabase } from '../supabase';
import { ShopContent, ShopMedia, ShopReview, ShopStory } from '../../types';

/**
 * Produktsidans innehåll: recensioner, kundbilder/-videor, "Kaffet" och
 * "Om oss". Sparas i site_content ('shop_content'): alla kan läsa, bara
 * admin kan ändra.
 */
const KEY = 'shop_content';

export const DEFAULT_SHOP_CONTENT: ShopContent = {
    reviews: [],
    media: [],
    coffee: {
        title: 'Kaffet',
        facts: ['100 % arabica', 'Ekologiskt', 'Rostat i Småland'],
        text: 'Vi rostar färskt i Småland tillsammans med ett gårdsrosteri. Bönorna är 100 % arabica och ekologiskt certifierade, och vi rostar varsamt för att lyfta fram varje bönas egen karaktär.',
    },
    about: {
        title: 'Om oss',
        text: 'CoffeeNova drivs från Jönköping. Vi gör riktigt bra kaffe enkelt och tillgängligt för alla, och låter klasser och lag sälja något de kan vara stolta över.',
    },
};

const str = (v: unknown, max: number) => (typeof v === 'string' ? v.trim().slice(0, max) : '');

const httpsUrl = (v: unknown) => { const u = str(v, 500); return /^https:\/\//.test(u) ? u : undefined; };

const story = (raw: any, fallback: ShopStory): ShopStory => ({
    title: str(raw?.title, 80) || fallback.title,
    text: str(raw?.text, 1500) || fallback.text,
    imageUrl: str(raw?.imageUrl, 500) || undefined,
    // Inte sparat än: standardfakta. Sparad tom lista: inga etiketter.
    facts: Array.isArray(raw?.facts)
        ? raw.facts.map((f: unknown) => str(f, 40)).filter(Boolean).slice(0, 4)
        : fallback.facts,
});

/** Läser och städar innehållet, så att butiken aldrig får trasiga värden. */
export const normalizeShopContent = (raw: any): ShopContent => {
    const reviews = (Array.isArray(raw?.reviews) ? raw.reviews : [])
        .map((r: any): ShopReview => {
            const media = (Array.isArray(r?.media) ? r.media : [])
                .map((m: any) => ({ url: httpsUrl(m?.url), kind: m?.kind === 'video' ? 'video' as const : 'image' as const }))
                .filter((m: { url?: string }): m is { url: string; kind: 'image' | 'video' } => !!m.url)
                .slice(0, 6);
            const created = str(r?.createdAt, 40);
            return {
                id: str(r?.id, 64) || Math.random().toString(36).slice(2),
                name: str(r?.name, 60),
                rating: Math.min(5, Math.max(1, Math.round(Number(r?.rating) || 5))),
                text: str(r?.text, 600),
                productId: str(r?.productId, 64) || undefined,
                published: r?.published !== false,
                avatarUrl: httpsUrl(r?.avatarUrl),
                media: media.length > 0 ? media : undefined,
                featured: r?.featured === true || undefined,
                createdAt: created && !Number.isNaN(Date.parse(created)) ? new Date(created).toISOString() : undefined,
            };
        })
        .filter((r: ShopReview) => r.name && r.text)
        // Högst en utvald per produkt: den första vinner.
        .map((r: ShopReview, _: number, all: ShopReview[]) => {
            if (!r.featured) return r;
            const first = all.find(x => x.featured && x.productId === r.productId);
            return first === r ? r : { ...r, featured: undefined };
        });
    return {
        reviews,
        media: (Array.isArray(raw?.media) ? raw.media : [])
            .map((m: any): ShopMedia => ({
                id: str(m?.id, 64) || Math.random().toString(36).slice(2),
                url: str(m?.url, 500),
                kind: m?.kind === 'video' ? 'video' : 'image',
                caption: str(m?.caption, 120) || undefined,
            }))
            .filter((m: ShopMedia) => /^https:\/\//.test(m.url)),
        coffee: story(raw?.coffee, DEFAULT_SHOP_CONTENT.coffee),
        about: story(raw?.about, DEFAULT_SHOP_CONTENT.about),
    };
};

export const getShopContent = async (): Promise<ShopContent> => {
    const { data, error } = await supabase.from('site_content').select('data').eq('key', KEY).maybeSingle();
    if (error || !data) return DEFAULT_SHOP_CONTENT;
    return normalizeShopContent(data.data);
};

export const saveShopContent = async (content: ShopContent) => {
    const { error } = await supabase.from('site_content').upsert({
        key: KEY,
        data: normalizeShopContent(content),
        updated_at: new Date().toISOString(),
    });
    if (error) throw error;
};
